import type { MaybeRefOrGetter } from 'vue';

import type { EntrySource, SelectedEntityInput } from '~/graphql/generated/graphql';

import { useProvideSignupEntryContext } from '~/composables/unified/useSignupEntryContext';
import { useSignupLink } from '~/composables/unified/useSignupLink';
import { EVENTS } from '~/constants/events';
import { UNIFIED_ROUTE_LOGIN } from '~/constants/routes/unified';
import { EntryCta } from '~/graphql/generated/graphql';

interface UseSignupCtaOptions {
  entrySource: MaybeRefOrGetter<EntrySource>;
  /** The `page` the CTA click is tracked under. */
  page: string;
  /**
   * The record the page shows, saved for the student on signup. Passing it also publishes the
   * page's entry context for the header's sign-up link. Until it resolves the link is the plain
   * login route.
   */
  selectedEntity?: MaybeRefOrGetter<SelectedEntityInput | null | undefined>;
  /** Extra properties the CTA click is tracked with, after the entry lane and page. */
  trackProps?: MaybeRefOrGetter<Record<string, string>>;
}

/**
 * The sign-up CTA a public page closes with: whether this visitor can still sign up, the signup
 * link carrying the page's entry lane, and the click tracking for the card.
 */
export const useSignupCta = ({ entrySource, page, selectedEntity, trackProps }: UseSignupCtaOptions) => {
  const { isLoggedIn, isGhostStudent } = useAuth();
  const { buildLink } = useSignupLink();
  const analytics = useAnalyticsService();

  const showSignUpCta = computed(() => !isLoggedIn.value || isGhostStudent.value);

  const signUpLink = computed(() => {
    if (selectedEntity === undefined) {
      return buildLink({ entrySource: toValue(entrySource), entryCta: EntryCta.StartChatting });
    }
    const entity = toValue(selectedEntity);
    return entity
      ? buildLink({ entrySource: toValue(entrySource), entryCta: EntryCta.StartChatting, selectedEntity: entity })
      : UNIFIED_ROUTE_LOGIN;
  });

  if (selectedEntity !== undefined) {
    useProvideSignupEntryContext(() => {
      const entity = toValue(selectedEntity);
      return entity ? { entrySource: toValue(entrySource), selectedEntity: entity } : null;
    });
  }

  const trackSignUpClick = () => {
    analytics.track(EVENTS.UNIFIED_PUBLIC_CTA_CLICKED, {
      entrySource: toValue(entrySource),
      entryCta: EntryCta.StartChatting,
      page,
      ...toValue(trackProps),
    });
  };

  return { showSignUpCta, signUpLink, trackSignUpClick };
};
